import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import HelpIcon from '../components/HelpIcon';
import { 
  ShoppingCart, Package, Truck, DollarSign, Calendar, Store, 
  ExternalLink, Clock, AlertTriangle, CheckCircle, Plus, RefreshCw,
  ArrowLeft, Sun, Moon, Search, Building, Eye, ChevronRight, Link2,
  TrendingUp, Filter, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-gray-500',
  ordered: 'bg-blue-500',
  received: 'bg-green-500',
  draft: 'bg-gray-400',
  confirmed: 'bg-blue-400',
  shipped: 'bg-indigo-500',
  in_production: 'bg-purple-500'
};

export default function ProcurementDashboard() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Purchase list data
  const [purchaseList, setPurchaseList] = useState(null);
  const [vendorFilter, setVendorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  
  // Vendors for PO creation
  const [vendors, setVendors] = useState([]);
  const [showCreatePODialog, setShowCreatePODialog] = useState(false);
  const [selectedPOVendor, setSelectedPOVendor] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchPurchaseList();
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchPurchaseList();
  }, [vendorFilter, statusFilter]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/procurement/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load procurement dashboard');
    }
  };

  const fetchPurchaseList = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (vendorFilter !== 'all') params.append('vendor_filter', vendorFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      
      const response = await axios.get(`${API}/api/purchase-list?${params}`);
      setPurchaseList(response.data);
    } catch (error) {
      console.error('Error fetching purchase list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/api/vendors`);
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectVendorItems = (vendorId) => {
    const vendorGroup = purchaseList?.by_vendor?.find(v => v.vendor_id === vendorId);
    if (vendorGroup) {
      const newSelected = new Set(selectedItems);
      vendorGroup.items.forEach(item => {
        if (item.purchase_status === 'pending') {
          newSelected.add(item.id);
        }
      });
      setSelectedItems(newSelected);
      setSelectedVendorId(vendorId);
    }
  };

  const createPOFromSelected = async () => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }
    if (!selectedPOVendor) {
      toast.error('Please select a vendor');
      return;
    }
    
    try {
      const selectedItem = purchaseList?.items?.find(i => selectedItems.has(i.id));
      await axios.post(`${API}/api/purchase-list/create-po`, {
        item_ids: Array.from(selectedItems),
        vendor_id: selectedPOVendor,
        project_id: selectedItem?.project_id
      });
      
      toast.success('Purchase Order created!');
      setShowCreatePODialog(false);
      setSelectedItems(new Set());
      setSelectedPOVendor('');
      fetchPurchaseList();
      fetchDashboard();
    } catch (error) {
      console.error('Error creating PO:', error);
      toast.error('Failed to create Purchase Order');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Filter purchase list items
  const filteredItems = purchaseList?.items?.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(query) ||
      (item.product_name || '').toLowerCase().includes(query) ||
      (item.spec_num || '').toLowerCase().includes(query) ||
      (item.vendor_name || '').toLowerCase().includes(query)
    );
  }) || [];

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#0D0D0D]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b px-6 py-3 flex items-center justify-between ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex items-center gap-2">
              <HelpIcon elementId="procurement-dashboard" position="inline" />
              <div>
                <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`}>Procurement</h1>
                <p className="text-sm text-muted-foreground">Purchase List & Procurement Dashboard</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => {
            setDarkMode(!darkMode);
            document.documentElement.classList.toggle('dark');
          }}>
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </div>
      
      <main className="p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="purchase-list">Purchase List</TabsTrigger>
              <TabsTrigger value="by-vendor">By Vendor</TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { fetchDashboard(); fetchPurchaseList(); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              {selectedItems.size > 0 && (
                <Button onClick={() => setShowCreatePODialog(true)} data-testid="create-po-selected-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create PO ({selectedItems.size} items)
                </Button>
              )}
            </div>
          </div>
          
          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboard && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                    <HelpIcon elementId="procurement-open-orders" position="top-right" />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <ShoppingCart className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.summary.open_pos_count}</p>
                          <p className="text-sm text-muted-foreground">Open POs</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(dashboard.summary.open_pos_value)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                    <HelpIcon elementId="procurement-pending-delivery" position="top-right" />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                          <Package className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.summary.items_awaiting}</p>
                          <p className="text-sm text-muted-foreground">Items Awaiting</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                    <HelpIcon elementId="report-deliveries" position="top-right" />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Truck className="w-5 h-5 text-purple-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.summary.this_week_deliveries}</p>
                          <p className="text-sm text-muted-foreground">This Week</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                    <HelpIcon elementId="procurement-total-value" position="top-right" />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{formatCurrency(dashboard.summary.total_value)}</p>
                          <p className="text-sm text-muted-foreground">Total Value</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  {/* Pending by Vendor */}
                  <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Store className="w-5 h-5" />
                        Pending POs by Vendor
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                      {dashboard.pending_by_vendor?.length > 0 ? (
                        dashboard.pending_by_vendor.map((vendor, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${darkMode ? 'bg-[#0D0D0D] hover:bg-[#252525]' : 'bg-gray-50 hover:bg-gray-100'}`}
                            onClick={() => navigate(`/purchase-orders?vendor=${vendor.vendor_id}`)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{vendor.vendor_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {vendor.pos.length} PO{vendor.pos.length !== 1 ? 's' : ''} • 
                                  Oldest: {vendor.oldest_po_date || 'N/A'}
                                </p>
                                {/* Show project names for each PO */}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {[...new Set(vendor.pos.map(po => po.project_name).filter(Boolean))].slice(0, 3).map((projName, i) => (
                                    <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-[#252525] text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                      {projName}
                                    </span>
                                  ))}
                                  {[...new Set(vendor.pos.map(po => po.project_name).filter(Boolean))].length > 3 && (
                                    <span className="text-xs text-muted-foreground">+{[...new Set(vendor.pos.map(po => po.project_name).filter(Boolean))].length - 3} more</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{formatCurrency(vendor.total_value)}</p>
                                <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground py-4">No pending POs</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Upcoming Deliveries */}
                  <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Truck className="w-5 h-5" />
                        Upcoming Deliveries
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                      {dashboard.upcoming_deliveries?.length > 0 ? (
                        dashboard.upcoming_deliveries.map((delivery, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'} ${delivery.is_overdue ? 'border border-red-500' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{delivery.po_number}</p>
                                <p className="text-sm text-muted-foreground">{delivery.vendor_name}</p>
                                {delivery.project_name && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded mt-1 inline-block ${darkMode ? 'bg-[#252525] text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                    {delivery.project_name}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge variant={delivery.is_overdue ? 'destructive' : delivery.days_until <= 3 ? 'warning' : 'secondary'}>
                                  {delivery.is_overdue ? `${Math.abs(delivery.days_until)} days overdue` : 
                                   delivery.days_until === 0 ? 'Today' : 
                                   `${delivery.days_until} day${delivery.days_until !== 1 ? 's' : ''}`}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-1">{delivery.delivery_date}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground py-4">No upcoming deliveries</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Budget Tracking */}
                <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Budget Tracking by Project
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {dashboard.budget_tracking?.length > 0 ? (
                        dashboard.budget_tracking.map((project, idx) => (
                          <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{project.project_name}</span>
                                <Badge variant="outline">{project.po_count} POs</Badge>
                              </div>
                              <span className="font-semibold">{project.percent_used.toFixed(1)}%</span>
                            </div>
                            <Progress value={Math.min(project.percent_used, 100)} className="h-2 mb-2" />
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>Spent: {formatCurrency(project.po_spend)}</span>
                              <span>Budget: {formatCurrency(project.total_budget)}</span>
                              <span className={project.remaining < 0 ? 'text-red-500' : 'text-green-500'}>
                                Remaining: {formatCurrency(project.remaining)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground py-4">No project budget data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
          
          {/* Purchase List Tab */}
          <TabsContent value="purchase-list" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={`pl-9 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                />
              </div>
              
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                  <SelectValue placeholder="Vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="with_vendor">With Vendor</SelectItem>
                  <SelectItem value="needs_vendor">Needs Vendor</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
              
              {purchaseList?.summary && (
                <div className="flex gap-2 ml-auto">
                  <Badge variant="secondary">{purchaseList.summary.total_items} items</Badge>
                  <Badge variant="outline" className="bg-yellow-500/10">{purchaseList.summary.pending} pending</Badge>
                  <Badge variant="outline" className="bg-blue-500/10">{purchaseList.summary.ordered} ordered</Badge>
                  <Badge variant="outline" className="bg-green-500/10">{purchaseList.summary.received} received</Badge>
                </div>
              )}
            </div>
            
            {/* Items List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : filteredItems.length === 0 ? (
              <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">No Items in Purchase List</h3>
                  <p className="text-muted-foreground">Items from estimates will appear here automatically</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredItems.map(item => (
                  <Card 
                    key={item.id} 
                    className={`transition-all ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''} ${selectedItems.has(item.id) ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {item.purchase_status === 'pending' && (
                          <Checkbox
                            checked={selectedItems.has(item.id)}
                            onCheckedChange={() => toggleItemSelection(item.id)}
                            data-testid={`select-item-${item.id}`}
                          />
                        )}
                        
                        {item.product_image && (
                          <img src={item.product_image} alt="" className="w-12 h-12 rounded object-cover" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {item.spec_num && (
                              <Badge variant="outline" className="text-xs font-mono">{item.spec_num}</Badge>
                            )}
                            <span className={`font-medium truncate ${darkMode ? 'text-white' : ''}`}>
                              {item.name || item.product_name}
                            </span>
                            <Badge className={`${STATUS_COLORS[item.purchase_status]} text-white text-xs`}>
                              {item.purchase_status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{item.quantity} {item.unit}</span>
                            <span>{formatCurrency(item.unit_cost)} ea</span>
                            {item.manufacturer && <span>{item.manufacturer}</span>}
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {item.project_name}
                            </span>
                          </div>
                          
                          {item.has_vendor ? (
                            <div className="flex items-center gap-2 mt-1">
                              <Store className="w-3 h-3 text-green-500" />
                              <span className="text-sm text-green-600">{item.vendor_name}</span>
                              {item.vendor_website && (
                                <a 
                                  href={item.vendor_website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="text-blue-500 hover:underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 mt-1 text-amber-500">
                              <AlertTriangle className="w-3 h-3" />
                              <span className="text-sm">Needs vendor assignment</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(item.total_cost)}</p>
                          {item.lead_time && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                              <Clock className="w-3 h-3" />
                              {item.lead_time}
                            </p>
                          )}
                          {item.po_info && (
                            <Badge 
                              variant="outline" 
                              className="text-xs mt-1 cursor-pointer"
                              onClick={() => navigate(`/purchase-orders`)}
                            >
                              {item.po_info.po_number}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* By Vendor Tab */}
          <TabsContent value="by-vendor" className="space-y-4">
            {purchaseList?.by_vendor?.length > 0 ? (
              purchaseList.by_vendor.map((vendorGroup, idx) => (
                <Card key={idx} className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Store className="w-5 h-5" />
                        {vendorGroup.vendor_name}
                        <Badge variant="secondary">{vendorGroup.items.length} items</Badge>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{formatCurrency(vendorGroup.total_value)}</span>
                        {vendorGroup.vendor_website && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(vendorGroup.vendor_website, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Website
                          </Button>
                        )}
                        {vendorGroup.vendor_id && (
                          <Button 
                            size="sm"
                            onClick={() => selectVendorItems(vendorGroup.vendor_id)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Select All Pending
                          </Button>
                        )}
                      </div>
                    </div>
                    {vendorGroup.vendor_email && (
                      <p className="text-sm text-muted-foreground">{vendorGroup.vendor_email}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {vendorGroup.items.map(item => (
                        <div 
                          key={item.id} 
                          className={`flex items-center gap-4 p-2 rounded ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'} ${selectedItems.has(item.id) ? 'ring-1 ring-blue-500' : ''}`}
                        >
                          {item.purchase_status === 'pending' && (
                            <Checkbox
                              checked={selectedItems.has(item.id)}
                              onCheckedChange={() => toggleItemSelection(item.id)}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {item.spec_num && <Badge variant="outline" className="text-xs font-mono">{item.spec_num}</Badge>}
                              <span className="font-medium">{item.name || item.product_name}</span>
                              <Badge className={`${STATUS_COLORS[item.purchase_status]} text-white text-xs`}>
                                {item.purchase_status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {item.quantity} {item.unit} • {item.project_name}
                            </p>
                          </div>
                          <span className="font-medium">{formatCurrency(item.total_cost)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <CardContent className="py-12 text-center">
                  <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">No Items Grouped by Vendor</h3>
                  <p className="text-muted-foreground">Items will be grouped by vendor as they're added</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Create PO Dialog */}
      <Dialog open={showCreatePODialog} onOpenChange={setShowCreatePODialog}>
        <DialogContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Creating PO for {selectedItems.size} selected item{selectedItems.size !== 1 ? 's' : ''}.
            </p>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Select Vendor</label>
              <Select value={selectedPOVendor} onValueChange={setSelectedPOVendor}>
                <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                  <SelectValue placeholder="Choose vendor for PO" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.company || `${v.first_name} ${v.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className={`p-3 rounded ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
              <p className="text-sm font-medium mb-2">Selected Items:</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {Array.from(selectedItems).map(itemId => {
                  const item = purchaseList?.items?.find(i => i.id === itemId);
                  return item ? (
                    <div key={itemId} className="text-sm flex justify-between">
                      <span>{item.spec_num || ''} {item.name || item.product_name}</span>
                      <span>{formatCurrency(item.total_cost)}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePODialog(false)}>Cancel</Button>
            <Button onClick={createPOFromSelected} disabled={!selectedPOVendor}>
              Create Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
